<template>
	<div
		class="offer-header-block"
		:class="{
			[`offer-header-block--subtype-${contentData.attributes?.subtype}`]:
				contentData.attributes?.subtype,
		}"
	>
		<div class="offer-header-block__panel">
			<div class="heading-wrapper">
				<ShrinkText
					:text="translate(contentData.name, true)"
					:fontSize="20"
				/>
			</div>
			<div class="offer-header-block__panel-btns">
				<AppIconButton
					v-if="isHideBtnVisible"
					class="offer-header-block__panel-btn offer-header-block-btn--visibility"
					:type="offerVisibilityIcon"
					@click="handleVisibilityChange"
					:iconSize="20"
				/>
				<AppIconButton
					class="offer-header-block__panel-btn offer-header-block-btn--close"
					:type="IconTypes.CLOSE"
					:iconSize="28"
					@click="emit('closePopup')"
				/>
			</div>
		</div>

		<div v-if="showRemainingTime" class="offer-header-block-timer">
			<Timer
				:time="formattedRemainingTime"
				:iconSize="16"
				:fontSize="16"
				hasIcon
			/>
		</div>
		<div class="offer-header-block-bg">
			<div
				class="offer-header-bg"
				:class="`offer-header-bg--${contentData.attributes?.skin}-410`"
			/>
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppIconButton from "@/components/AppIconButton.vue";
import ShrinkText from "@/components/ShrinkText.vue";
import Timer from "@/components/Timer.vue";
import useShopStore from "@/store/useShopStore";
import { OfferTemplate, type ShopItem } from "@/types/shop";
import { IconTypes } from "@/types/ui";
import { convertUnixTimeToRemainingSeconds } from "@/utils/format";
import { roundNumber } from "@/utils/number";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed } from "vue";

interface Props {
	contentData: ShopItem;
}

const { contentData } = defineProps<Props>();

const emit = defineEmits(["closePopup"]);

const shopStore = useShopStore();
const { hiddenLeftBoxOffers, provisionOffers } = storeToRefs(shopStore);

const showRemainingTime = computed((): boolean => {
	if (contentData.attributes?.hide_remaining_time) return false;
	return !!contentData.end_date && formattedRemainingTime.value > 0;
});

const formattedRemainingTime = computed((): number => {
	return convertUnixTimeToRemainingSeconds(
		roundNumber(new Date(contentData.end_date as string).getTime() / 1_000),
	);
});

const offerVisibilityIcon = computed((): IconTypes => {
	return hiddenLeftBoxOffers.value.includes(contentData?.store_id)
		? IconTypes.SHOW // is hidden >> SHOW icon
		: IconTypes.HIDE; // is visible >> HIDE icon
});

const isHideBtnVisible = computed((): boolean => {
	if (hiddenLeftBoxOffers.value.includes(contentData?.store_id)) return false;
	if (contentData.attributes?.template === OfferTemplate.PROVISION) {
		return !provisionOffers.value?.some(
			(provisionOffer: ShopItem): boolean =>
				hiddenLeftBoxOffers.value.includes(provisionOffer.store_id),
		);
	}
	return true;
});

const handleVisibilityChange = (): void => {
	const isHidden = hiddenLeftBoxOffers.value.includes(contentData.store_id);

	const updated = isHidden
		? hiddenLeftBoxOffers.value.filter(
				(id: string): boolean => id !== contentData.store_id,
			)
		: [...hiddenLeftBoxOffers.value, contentData.store_id];

	emit("closePopup"); // close popup first

	setTimeout((): void => {
		// then hide left box after 0.5 seconds
		shopStore.setHiddenLeftBoxOffers(updated);
	}, 500);
};
</script>

<style lang="scss" scoped>
.offer-header-block {
	position: relative;
	z-index: 1; /* header = 1 */
	width: 100%;
	height: 11.625rem;

	&--subtype {
		&-welcome_back_offer {
			.offer-header-bg {
				@include background(
					url($path-images + "deeplink/welcome-back/backgrounds/welcome-back-offer-header-bg.avif"),
					100% 100%,
					center,
					no-repeat
				);
			}
		}
	}

	&__panel {
		position: relative;
		z-index: 1;
		width: 100%;
		@include flex-row-between;
		gap: 1rem;

		.heading-wrapper {
			position: relative;
			flex: 1 1 auto;
			padding-left: 0.5rem;
			overflow: hidden;

			:deep(.text-container) {
				@include flex-row-start;
			}

			:deep(.shrink-text) {
				color: #f4d278;
				font-weight: 800;
				text-align: left;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
				text-transform: uppercase;
			}
		}

		&-btns {
			position: relative;
			flex-grow: 0;
			@include flex-row-end;
			gap: 0.5rem;

			.offer-header-block__panel-btn {
				position: relative;
				background-image: linear-gradient(to top, #181a1d, #303239);
				width: 1.875rem;
				height: 1.875rem;

				&.offer-header-block-btn--visibility {
					margin-top: -1.25rem;
					box-shadow: inset 0 0 0.5rem 0 rgba(0, 0, 0, 0.3);
					border-width: 0.075rem;
					border-style: solid;
					border-image-source: linear-gradient(
						to top,
						#bd9032,
						#f0d386
					);
					border-image-slice: 1;
					background-image: linear-gradient(to top, #181a1d, #303239);
				}

				&.offer-header-block-btn--close {
					margin-top: -0.75rem;
					margin-right: -0.625rem;
					width: 2.5rem;
					height: 2.5rem;
				}
			}
		}
	}

	&-timer {
		display: inline-flex;
		position: relative;
		top: 0;
		left: 0;
		justify-content: center;
		align-items: flex-start;
		z-index: 1;
		mask-image: linear-gradient(
			to right,
			black 0%,
			black 20%,
			black 70%,
			transparent 98%
		);
		background-image: linear-gradient(
			to right,
			rgba(85, 0, 20, 1),
			rgba(85, 0, 20, 0.8),
			rgba(85, 0, 20, 0.7)
		);
		padding: 0 0.375rem;
		padding-right: 2.25rem;
		width: auto;
		height: 1.625rem;

		&::before,
		&::after {
			position: absolute;
			left: 0;
			z-index: 1;
			background: #cf0032;
			width: 100%;
			height: 0.063rem;
			content: "";
		}

		&::before {
			top: 0;
		}

		&::after {
			bottom: 0;
		}

		:deep(.timer__time) {
			padding-bottom: 0.125rem;
			color: #f2dcb5;
			font-size: 1rem;
		}
	}

	&-bg {
		display: flex;
		position: absolute;
		top: 0;
		left: 0;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		width: 100%;
		height: 100%;
		overflow: hidden;
	}
}
</style>
